// ConnectorInsert — the edit-mode "insert a step here" affordance that lives ON
// a connector between two steps (flow/sequence gap; BPMN seq edge).
//
// One "+" per gap: inserting "after the previous step" and "before the next"
// are the same edit, so the affordance sits at the connector midpoint and calls
// the step-edit api's `addStepAfter(sourceNodeId, text)` (or `insertInto`). A
// click expands the dot into an inline input; submit commits and collapses,
// Escape cancels. Pure affordance — no model state; the api's recompile
// invariant is the last guard.
//
// Reveal is LOCAL, not global: each affordance owns a `hover` zone (a
// transparent rect over the gap it fills) and only fades its line + "+" in while
// THAT zone is hovered — the Confluence column-insert model. Hovering the board
// no longer lights up every gap at once. The `line` (drawn on hover) shows where
// the new step lands: a vertical segment along a flow connector, a horizontal
// rule across a sequence row gap.

import { useState } from "react";
import { Button } from "@archlang/ui";
import { matchScaffolds, type InsertEnclosing } from "./insertScaffolds.js";

/** A rect in SVG user space. */
export interface InsertRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** A line segment in SVG user space (the connector the "+" rides on). */
export interface InsertLine {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

export interface ConnectorInsertProps {
  /** "+" anchor (its centre) in SVG user space. */
  readonly x: number;
  readonly y: number;
  /** Commit the typed step text; returns whether it applied (true → collapse). */
  readonly onSubmit: (text: string) => boolean;
  /** Placeholder / example shown in the input. */
  readonly label?: string;
  /** The local hover target — the gap this "+" fills. Hovering it reveals the
   *  line + "+". Defaults to a small box around the anchor when omitted. */
  readonly hover?: InsertRect;
  /** The connector segment drawn (on hover) to show where the step lands. */
  readonly line?: InsertLine;
  /** Which construct encloses this insertion point — filters the palette to the
   *  contextually-legal scaffolds (a `select` body offers cases, not steps).
   *  Defaults to `flow` (the step palette). */
  readonly enclosing?: InsertEnclosing;
  /** Debug/test marker for the GAP kind this "+" fills (after-step, after-block,
   *  into) — surfaced as `data-insert-kind`. Purely diagnostic. */
  readonly kind?: string;
}

const DOT = 16;
const POP_W = 232;
const POP_H = 108;
/** Default hover-zone half-extent when the caller gives no explicit rect. */
const ZONE_PAD = 14;

export function ConnectorInsert({ x, y, onSubmit, label = "A > B", hover, line, enclosing = "flow", kind }: ConnectorInsertProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [value, setValue] = useState("");
  // The scaffold of the currently-hovered palette chip — drives the ghost
  // preview so the structural result (brace-form control blocks) is visible in
  // place, at the insertion "+", before commit (plan §128).
  const [hoverChip, setHoverChip] = useState<string | null>(null);

  const w = open ? POP_W : DOT;
  const h = open ? POP_H : DOT;
  // Collapse the popover, clearing the ephemeral ghost so a later reopen never
  // shows a stale preview from the previous session.
  const close = () => {
    setOpen(false);
    setHoverChip(null);
  };
  // Commit a step line (typed input OR a palette-chip scaffold) → collapse on apply.
  const insert = (text: string) => {
    if (text.trim() === "") return;
    if (onSubmit(text)) {
      setValue("");
      close();
    }
  };
  const chips = matchScaffolds(value, enclosing);
  // Ghost the hovered chip; otherwise ghost a typed step (not a `/slash` filter).
  const typed = value.trim();
  const previewText = hoverChip ?? (typed !== "" && !typed.startsWith("/") ? typed : null);

  const zone: InsertRect = hover ?? { x: x - ZONE_PAD, y: y - ZONE_PAD, w: ZONE_PAD * 2, h: ZONE_PAD * 2 };
  const revealed = hovered || open;

  return (
    // The overlay this rides in is `pointer-events-none`; the zone rect + "+"
    // opt back in (`pointerEvents: auto`) so they are hoverable/clickable, while
    // the connector line stays inert. onMouseOver/Out with a relatedTarget guard
    // keep `hovered` true across internal moves (zone → line → "+") and only
    // clear it when the pointer leaves the whole affordance.
    <g
      data-insert-kind={kind}
      onMouseOver={() => setHovered(true)}
      onMouseOut={(e) => {
        if (!(e.currentTarget as SVGGElement).contains(e.relatedTarget as Node | null)) setHovered(false);
      }}
    >
      <rect
        x={zone.x}
        y={zone.y}
        width={zone.w}
        height={zone.h}
        fill="transparent"
        style={{ pointerEvents: "auto" }}
        data-connector-insert-zone=""
      />
      {revealed && line && (
        <line
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          className="stroke-sky-500"
          strokeWidth={2}
          strokeLinecap="round"
          style={{ pointerEvents: "none" }}
          data-connector-insert-line=""
        />
      )}
      <foreignObject
        x={x - w / 2}
        y={y - h / 2}
        width={w}
        height={h}
        style={{ overflow: "visible", pointerEvents: revealed ? "auto" : "none" }}
        onClick={(e) => e.stopPropagation()}
      >
        {open ? (
          <div
            className="rounded-md border border-sky-500/70 bg-slate-950 p-1 shadow-xl shadow-black/50"
            data-connector-insert-popover=""
          >
            {/* Typed step (or `/if` slash-filter). Enter inserts the typed text. */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                insert(value.trim());
              }}
            >
              <input
                autoFocus
                value={value}
                placeholder={label}
                onChange={(e) => setValue(e.target.value)}
                onBlur={close}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    setValue("");
                    close();
                  }
                }}
                className="w-full rounded-sm border border-slate-700 bg-slate-900 px-1.5 py-0.5 text-xs text-slate-100 outline-none focus:border-sky-500"
                data-connector-insert-input=""
              />
            </form>
            {/* Palette — one chip per construct; a chip inserts its scaffold.
                `onMouseDown preventDefault` keeps the input focused so its `onBlur`
                doesn't collapse the popover before the click lands. */}
            <div className="mt-1 flex flex-wrap gap-0.5">
              {chips.map((s) => (
                <Button
                  key={s.key}
                  variant="secondary"
                  size="xs"
                  data-connector-insert-chip={s.key}
                  title={s.text}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHoverChip(s.text)}
                  onMouseLeave={() => setHoverChip(null)}
                  onClick={() => insert(s.text)}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            {/* Ghost preview — the exact scaffold that will be inserted, dimmed,
                in place at the "+". Multi-line for brace-form control blocks. */}
            {previewText && (
              <pre
                data-insert-preview=""
                className="mt-1 max-h-24 overflow-auto whitespace-pre rounded-sm border border-slate-800 bg-slate-900/60 px-1.5 py-1 text-[10px] leading-tight text-slate-500"
              >
                {previewText}
              </pre>
            )}
          </div>
        ) : (
          <button
            type="button"
            aria-label="Insert step here"
            title="Insert step here"
            data-connector-insert=""
            onClick={() => {
              setHoverChip(null);
              setOpen(true);
            }}
            className={`flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 bg-slate-900 text-[11px] leading-none text-slate-400 transition-opacity hover:border-sky-500 hover:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${revealed ? "opacity-100" : "opacity-0"}`}
          >
            +
          </button>
        )}
      </foreignObject>
    </g>
  );
}
